/**
 * Firebase Configuration and Initialization
 * 
 * This file initializes Firebase with configuration from environment variables.
 * Exports Firebase services: auth, database, and storage.
 */

import { initializeApp } from "firebase/app";
import { getDatabase } from "firebase/database";
import { getStorage } from "firebase/storage";
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  signOut,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
} from "firebase/firestore";
import { env, validateRequiredEnv } from "../config/env";

validateRequiredEnv();

const firebaseConfig = {
  apiKey: env.apiKey,
  authDomain: env.authDomain,
  databaseURL: env.databaseURL,
  projectId: env.projectId,
  storageBucket: env.storageBucket,
  messagingSenderId: env.messagingSenderId,
  appId: env.appId,
  measurementId: env.measurementId,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);
const storage = getStorage(app);

function removeStaleAuthLocalStorage() {
  if (typeof window === "undefined" || !env.apiKey) return;
  try {
    const prefixForThisApp = `firebase:authUser:${env.apiKey}:`;
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("firebase:authUser:")) continue;
      if (!key.startsWith(prefixForThisApp)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

removeStaleAuthLocalStorage();

function getOrInitAuth() {
  if (typeof window === "undefined") {
    return getAuth(app);
  }
  try {
    return initializeAuth(app, { persistence: browserLocalPersistence });
  } catch (e) {
    if (e?.code === "auth/already-initialized") {
      return getAuth(app);
    }
    throw e;
  }
}

const auth = getOrInitAuth();

// Drop invalid persisted sessions (e.g. after switching Firebase project / env) so STS token refresh does not 400 every boot.
if (typeof window !== "undefined") {
  void auth.authStateReady().then(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await user.getIdToken(true);
    } catch (err) {
      // A 400 from securetoken means the saved refresh token is dead
      // (deleted user, cleared project, or expired session). Keep network blips.
      if (err?.code === "auth/network-request-failed") return;
      try {
        await signOut(auth);
      } catch {
        /* ignore */
      }
    }
  });
}

// Firestore with persistent cache (replaces deprecated enableIndexedDbPersistence)
let firestore;
if (typeof window !== "undefined") {
  try {
    firestore = initializeFirestore(app, {
      localCache: persistentLocalCache({}),
    });
  } catch (err) {
    // failed-precondition = multiple tabs; unimplemented = browser doesn't support
    firestore = getFirestore(app);
  }
} else {
  firestore = getFirestore(app);
}

export { app, db, storage, auth, firestore };

